import { useEffect, useRef, useState } from 'react';
import { ChevronRight, ShieldAlert, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { MODULE_LABELS } from '@/utils/modules';
import { useSidePanel } from './SidePanelContext';
import { PANEL_VIEWS } from './registry';

/**
 * Split-View-Seitenleiste: auf dem Desktop letzte Spalte im Shell-Fluss
 * (kein Overlay, kein z-Index), mobil (< md) vollbildiges Overlay über
 * dem Inhalt. Eigene Tab-Leiste, per Ziehgriff anpassbare Breite (nur
 * Desktop), Modul-Gating für die aktive View. Beim Schließen wird der
 * Inhalt unmountet; Wiederöffnen restauriert die Tabs.
 */
export function SidePanel() {
  const { tabs, activeId, isOpen, width, closeTab, setActive, closePanel, setWidth } = useSidePanel();
  const { hasModule } = useAuth();
  const [resizing, setResizing] = useState(false);
  const resizingRef = useRef(false);

  // Escape schließt den aktiven Tab (letzter Tab → Panel zu). Während des
  // Ziehens nicht reagieren.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !resizingRef.current && activeId) {
        closeTab(activeId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, activeId, closeTab]);

  if (!isOpen) return null;

  const activeTab = tabs.find((t) => t.id === activeId) ?? null;
  const activeDef = activeTab
    ? (PANEL_VIEWS[activeTab.view as keyof typeof PANEL_VIEWS] ?? null)
    : null;
  const gated = activeDef?.module ? !hasModule(activeDef.module) : false;

  const handleResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizingRef.current = true;
    setResizing(true);
  };
  const handleResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizingRef.current) return;
    setWidth(window.innerWidth - e.clientX);
  };
  const handleResizeEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    setResizing(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Pointer war schon weg — egal.
    }
  };

  return (
    <div
      className="relative flex w-full shrink-0 flex-col bg-white border-l border-gray-200 max-md:fixed max-md:inset-y-0 max-md:right-0 max-md:z-40 max-md:shadow-xl md:w-[var(--panel-w)] md:max-w-[60vw]"
      style={{ '--panel-w': `${width}px` } as React.CSSProperties}
    >
      {/* Ziehgriff links (nur Desktop) */}
      <div
        className={`hidden md:block absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize transition-colors ${
          resizing ? 'bg-primary-100' : 'hover:bg-primary-100'
        }`}
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
        onPointerCancel={handleResizeEnd}
      />

      {/* Tab-Leiste */}
      <div className="flex items-stretch border-b border-gray-200 overflow-x-auto">
        {tabs.map((tab) => {
          const def = PANEL_VIEWS[tab.view as keyof typeof PANEL_VIEWS];
          const Icon = def?.icon ?? ShieldAlert;
          const active = tab.id === activeId;
          return (
            <div
              key={tab.id}
              className={`flex items-center gap-1 pl-4 pr-1 shrink-0 border-r border-gray-100 max-w-[240px] border-b-2 ${
                active ? 'border-b-primary-600 text-gray-900' : 'border-b-transparent text-gray-500'
              }`}
            >
              <button
                type="button"
                onClick={() => setActive(tab.id)}
                className="flex min-w-0 flex-1 items-center gap-2 py-2.5 text-left text-sm"
                title={tab.title}
              >
                <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                <span className="truncate">{tab.title}</span>
              </button>
              <button
                type="button"
                onClick={() => closeTab(tab.id)}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                title="Tab schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={closePanel}
          className="ml-auto shrink-0 px-3 py-2 text-gray-400 hover:text-gray-600"
          title="Leiste schließen"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Inhalt: nur der aktive Tab ist gemountet (Tab-Wechsel = Refetch) */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab && activeDef && (
          gated ? (
            <div className="card p-8 text-center text-gray-500">
              <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-gray-400" />
              <p>Modul „{MODULE_LABELS[activeDef.module]}“ ist für Ihr Konto deaktiviert.</p>
            </div>
          ) : (
            <activeDef.Component params={activeTab.params ?? {}} close={() => closeTab(activeTab.id)} />
          )
        )}
      </div>
    </div>
  );
}